import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from './client';

/**
 * Accomplishment moments — the server's proven facts about the signed-in person, plus their off
 * switch and seen-once record (`GET` / `PATCH /my-moments`). Fleet primitive:
 * ptc-statetax/docs/moments-primitive.md. The browser renders; it never decides a fact.
 */

export interface CompletedReviewFact {
  id: string;
  reviewId: string;
  clientName: string;
  periodStart: string;
  periodEnd: string;
  completedAt: string;
}

export interface TrimesterClosedFact {
  id: string;
  bookkeepingId: string;
  clientName: string;
  trimester: 'T1' | 'T2' | 'T3';
  year: number;
  start: string;
  end: string;
  closedByReviewId: string;
  closedAt: string;
}

export type MyMoments =
  | { available: false }
  | {
      available: true;
      enabled: boolean;
      seen: string[];
      reviewsCompleted: CompletedReviewFact[];
      trimestersClosed: TrimesterClosedFact[];
    };

export const MY_MOMENTS_QUERY_KEY = ['my-moments'] as const;

export function useMyMoments() {
  return useQuery<MyMoments>({
    queryKey: MY_MOMENTS_QUERY_KEY,
    queryFn: () => fetchApi<MyMoments>('/my-moments'),
    // A celebration is never worth a retry storm or an error on screen.
    retry: false,
    staleTime: 60_000,
  });
}

export interface MomentSettingsPatch {
  enabled?: boolean | null;
  seen?: string[] | null;
}

/**
 * Saves ONLY the fields given — the server merges field by field, so writing the off switch never
 * wipes the seen record. The cache is updated first, so a moment cannot fire twice while the write
 * is in flight.
 */
export function useSaveMomentSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: MomentSettingsPatch) =>
      fetchApi<{ enabled: boolean; seen: string[] }>('/my-moments', { method: 'PATCH', body: JSON.stringify(patch) }),
    onMutate: (patch) => {
      queryClient.setQueryData<MyMoments>(MY_MOMENTS_QUERY_KEY, (old) =>
        old && old.available
          ? {
              ...old,
              ...(patch.enabled !== undefined ? { enabled: patch.enabled !== false } : {}),
              ...(patch.seen !== undefined ? { seen: patch.seen ?? [] } : {}),
            }
          : old,
      );
    },
  });
}
